import { STATUS_CODES } from 'utils/Constants'
import { getMiddleware, MiddlewareFunction } from './index'
import { Facets, Keyword, getQueryLocation, RESULTS_TYPES, PROVIDERS } from 'utils/directoryParser'
import { isJobsUrl } from './utils'

import DirectoryQuery from './query.directory.middleware.gql'

export const handleDirectoryPageRedirects: MiddlewareFunction = async (
  { nextContext, apolloClient },
  next
) => {
  const { req, query } = nextContext

  const pageValue = query?.page
  const pageNumber = isNaN(Number(pageValue)) ? null : Number(pageValue)
  const isDirectoryNearUrl = query?.locations === 'near'
  const isDirectoryPaginationUrl = typeof pageNumber === 'number'

  /**
   * The current directory middlewares only deal with /near and directory urls with pagination.
   * If we add additional middlewares for directories we'll need smarter url guards.
   */
  if (!isDirectoryNearUrl && !isDirectoryPaginationUrl) {
    return next()
  }

  if (pageNumber > 1000) {
    const res = nextContext.res
    res.statusCode = 404

    return {
      isServerSideRedirect: false,
      errorCode: STATUS_CODES.CODE404,
    }
  }

  const location = getQueryLocation(nextContext)
  const keyword = Keyword(nextContext)

  const directoryProviderKey = Object.keys(PROVIDERS).find((provider) => {
    const providerSlug = PROVIDERS[provider].facetSlug
    return req?.url?.includes(providerSlug)
  })
  const directoryProvider = PROVIDERS[directoryProviderKey]

  const queryVariables = {
    params: {
      page: pageNumber || null,
      type: isJobsUrl(req) ? RESULTS_TYPES.JOBS : RESULTS_TYPES.PROFILES,
      selectedFacets: Facets(nextContext),
      location: {
        country: location?.country ?? null,
        state: location?.state ?? null,
        city: location?.city ?? null,
        neighborhood: location?.neighborhood ?? null,
        postalCode: null,
        range: null,
      },
      useNear: location?.useNear ?? false,
      search: keyword || null,
      selectedLanguageId: null,
      selectedProviderTypes: directoryProvider?.facetKeyword || null,
    },
  }

  const { data } = await apolloClient.query({
    query: DirectoryQuery,
    variables: queryVariables,
  })

  const directoryMiddlewares = [handleDirectoryNearNoListings, handleDirectoryPageNoListings]

  const result = await getMiddleware(directoryMiddlewares)({ data, nextContext, apolloClient })

  if (!result) {
    return next()
  } else {
    return result
  }
}

export const handleDirectoryNearNoListings: MiddlewareFunction = async (
  { data, nextContext },
  next
) => {
  const { req, res, query } = nextContext
  const listingsCount = data?.site?.directory?.count ?? 0
  const hasListings = listingsCount > 0
  const isDirectoryNearUrl = query?.locations === 'near'

  if (!isDirectoryNearUrl) {
    return next()
  }

  if (hasListings) {
    return next()
  }

  res.writeHead(STATUS_CODES.CODE302, {
    Location: req.url.replace('/near', ''),
  })
  res.end()

  return {
    isServerSideRedirect: true,
    errorCode: null,
  }
}

export const handleDirectoryPageNoListings: MiddlewareFunction = async (
  { data, nextContext },
  next
) => {
  const listingsCount = data?.site?.directory?.count ?? 0
  const hasListings = listingsCount > 0

  if (hasListings) {
    return next()
  }

  const res = nextContext.res
  res.statusCode = 404

  return {
    isServerSideRedirect: false,
    errorCode: STATUS_CODES.CODE404,
  }
}
